import { useState, useEffect } from 'react';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function IntegrationCard({ icon, name, description, connected, connectedText, status, onAction }) {
  // status: 'connected' | 'available' | 'coming_soon'
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '1rem',
      padding: '1.25rem',
      background: connected ? 'var(--accent-dark)' : 'var(--bg-3)',
      border: `1px solid ${connected ? 'rgba(0,255,136,0.2)' : 'var(--border)'}`,
      borderRadius: 10,
    }}>
      <div style={{ width: 44, height: 44, background: 'var(--bg-2)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0, border: '1px solid var(--border)' }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 3 }}>{name}</div>
        <div style={{ fontSize: 12, color: connected ? 'var(--accent)' : 'var(--text-3)' }}>
          {connected ? `✓ ${connectedText}` : description}
        </div>
      </div>
      {status === 'connected' && (
        <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--accent)', background: 'var(--accent-dark)', border: '1px solid rgba(0,255,136,0.2)', padding: '3px 10px', borderRadius: 20 }}>Connected</span>
      )}
      {status === 'available' && (
        <button className="btn btn-outline btn-sm" onClick={onAction}>Connect</button>
      )}
      {status === 'coming_soon' && (
        <span style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--yellow)', background: 'var(--yellow-dim)', border: '1px solid rgba(255,204,0,0.2)', padding: '3px 10px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Coming Soon</span>
      )}
    </div>
  );
}

function InfoRow({ label, value, mono }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontSize: 13, color: 'var(--text-2)' }}>{label}</span>
      <span style={{ fontSize: 12, fontFamily: mono ? 'var(--mono)' : 'inherit', color: 'var(--text-3)', background: 'var(--bg-3)', padding: '2px 8px', borderRadius: 4 }}>{value}</span>
    </div>
  );
}

export default function SettingsPage() {
  const [health, setHealth] = useState(null);
  const [zoomStatus, setZoomStatus] = useState(null);

  useEffect(() => {
    fetch(`${BASE}/api/health`).then(r => r.json()).then(setHealth).catch(console.error);
    fetch(`${BASE}/api/zoom/status`).then(r => r.json()).then(setZoomStatus).catch(console.error);
  }, []);

  return (
    <div className="page fade-in">
      <div style={{ marginBottom: '1.75rem' }}>
        <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 4 }}>Settings</div>
        <div style={{ fontSize: 13, color: 'var(--text-2)' }}>Manage your integrations and preferences</div>
      </div>

      {/* System Status */}
      <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', marginBottom: '1.25rem' }}>
        <div style={{ padding: '0.875rem 1.25rem', borderBottom: '1px solid var(--border)' }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-2)' }}>⚡ System Status</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '0.75rem', padding: '1.25rem' }}>
          {[
            { key: 'gemini',   label: 'AI Analysis',         desc: 'Meeting intelligence' },
            { key: 'deepgram', label: 'Audio Transcription',  desc: 'Voice to text' },
            { key: 'resend',   label: 'Email Summaries',      desc: 'Auto-send reports' },
            { key: 'slack',    label: 'Slack Notifications',  desc: 'Team alerts' },
          ].map(item => {
            const active = health?.env?.[item.key];
            return (
              <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.875rem 1rem', background: 'var(--bg-3)', border: `1px solid ${active ? 'rgba(0,255,136,0.15)' : 'var(--border)'}`, borderRadius: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: active ? 'var(--accent)' : 'var(--border-2)', flexShrink: 0, boxShadow: active ? '0 0 8px var(--accent)' : 'none' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2 }}>{item.label}</div>
                  <div style={{ fontSize: 11, color: active ? 'var(--accent)' : 'var(--text-3)' }}>
                    {active ? 'Active' : 'Not active'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Integrations */}
      <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', marginBottom: '1.25rem' }}>
        <div style={{ padding: '0.875rem 1.25rem', borderBottom: '1px solid var(--border)' }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-2)' }}>🔌 Integrations</span>
        </div>
        <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <IntegrationCard
            icon="🎥" name="Zoom" description="Import transcripts from your cloud recordings"
            connected={zoomStatus?.connected} connectedText={zoomStatus?.email || 'Connected'}
            status={zoomStatus?.connected ? 'connected' : 'coming_soon'}
          />
          <IntegrationCard
            icon="💬" name="Slack" description="Post action items and daily reminders to your team"
            connected={health?.env?.slack} connectedText="Webhook active"
            status={health?.env?.slack ? 'connected' : 'coming_soon'}
          />
          <IntegrationCard
            icon="✉️" name="Email" description="Automatically send meeting summaries to attendees"
            connected={health?.env?.resend} connectedText="Resend active"
            status={health?.env?.resend ? 'connected' : 'coming_soon'}
          />
          <IntegrationCard
            icon="📋" name="Asana" description="Create tasks in Asana from action items"
            connected={false} connectedText=""
            status="coming_soon"
          />
          <IntegrationCard
            icon="📝" name="Notion" description="Save meeting summaries to your Notion workspace"
            connected={false} connectedText=""
            status="coming_soon"
          />
          <IntegrationCard
            icon="🗂️" name="Google Calendar" description="Auto-detect upcoming meetings and send reminders"
            connected={false} connectedText=""
            status="coming_soon"
          />
        </div>
      </div>

      {/* App Info */}
      <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ padding: '0.875rem 1.25rem', borderBottom: '1px solid var(--border)' }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-2)' }}>ℹ About</span>
        </div>
        <div style={{ padding: '0 1.25rem' }}>
          <InfoRow label="Version" value="2.0.0" mono />
          <InfoRow label="AI Model" value="Gemini 1.5 Flash" mono />
          <InfoRow label="Transcription" value="Deepgram Nova-2" mono />
          <InfoRow label="Status" value={health ? '● All systems operational' : '○ Checking...'} mono />
        </div>
      </div>
    </div>
  );
}
